import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeftRight, ChevronRight, Coins } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { PURITY_MULTIPLIERS } from '@/lib/constants';
import { FALLBACK_METAL_PRICES } from '@/server/config/ssr-config';
import { STRUMENTI_ORO, type StrumentoVariant } from '@/lib/strumentiOroData';

const TROY_OUNCE_G = 31.1034768;

const KARAT_OPTIONS = [
  { label: '24KT (puro 999‰)', value: '24KT' as const, mult: PURITY_MULTIPLIERS['24KT'] },
  { label: '22KT (sterline)', value: '22KT' as const, mult: PURITY_MULTIPLIERS['22KT'] },
  { label: '18KT (750‰)', value: '18KT' as const, mult: PURITY_MULTIPLIERS['18KT'] },
  { label: '14KT (585‰)', value: '14KT' as const, mult: PURITY_MULTIPLIERS['14KT'] },
  { label: '9KT (375‰)', value: '9KT' as const, mult: PURITY_MULTIPLIERS['9KT'] },
];

interface Props {
  slug: keyof typeof STRUMENTI_ORO;
  ssrMetalPrices?: any;
}

const fmt = (v: number, d = 2) =>
  v.toLocaleString('it-IT', { minimumFractionDigits: d, maximumFractionDigits: d });

export default function StrumentoOroPage({ slug, ssrMetalPrices }: Props) {
  const meta = STRUMENTI_ORO[slug];
  const { currentGoldPrice } = useStore();
  const goldPrice24K =
    currentGoldPrice?.price ??
    ssrMetalPrices?.gold?.price ??
    FALLBACK_METAL_PRICES.gold.price;

  if (!meta) return null;

  const canonical = `https://www.gildy.it${meta.path}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: meta.h1,
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Web',
    description: meta.metaDesc,
    url: canonical,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
  };

  const breadcrumbsLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.gildy.it/' },
      { '@type': 'ListItem', position: 2, name: meta.h1, item: canonical },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{meta.titleTag}</title>
        <meta name="description" content={meta.metaDesc} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta property="og:title" content={meta.titleTag} />
        <meta property="og:description" content={meta.metaDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="it_IT" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbsLd)}</script>
      </Helmet>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <nav className="flex items-center text-sm text-muted-foreground mb-4">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="h-4 w-4 mx-1" />
          <span className="text-foreground">{meta.h1}</span>
        </nav>

        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{meta.h1}</h1>
        <p className="text-lg text-muted-foreground leading-relaxed mb-8">{meta.intro}</p>

        <ToolWidget variant={meta.variant} goldPrice24K={goldPrice24K} />

        <section className="mt-10 space-y-4 text-muted-foreground">
          <h2 className="text-2xl font-bold text-foreground">Risorse correlate</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><Link to="/calcolatore-oro-usato" className="text-gold underline">Calcolatore oro usato completo</Link></li>
            <li><Link to="/prezzo-oro-oggi" className="text-gold underline">Prezzo oro oggi (live)</Link></li>
            <li><Link to="/compro-oro-vicino-a-me" className="text-gold underline">Compro oro vicino a te</Link></li>
          </ul>
        </section>
      </div>
    </>
  );
}

function ToolWidget({ variant, goldPrice24K }: { variant: StrumentoVariant; goldPrice24K: number }) {
  switch (variant) {
    case 'once-grammi':
      return <OnceGrammiTool goldPrice24K={goldPrice24K} />;
    case 'grammi-euro-oro':
      return <GrammiEuroTool goldPrice24K={goldPrice24K} />;
    case 'quanto-vale-1g':
      return <QuantoVale1gTool goldPrice24K={goldPrice24K} />;
    case 'quanto-vale-18kt':
      return <QuantoVale18ktTool goldPrice24K={goldPrice24K} />;
  }
}

function OnceGrammiTool({ goldPrice24K }: { goldPrice24K: number }) {
  const [oncia, setOncia] = useState('1');
  const grammi = (parseFloat(oncia.replace(',', '.')) || 0) * TROY_OUNCE_G;
  const valoreEuro = grammi * goldPrice24K;
  return (
    <Card className="border-gold/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowLeftRight className="h-5 w-5 text-gold" /> Convertitore once → grammi
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="oz">Once troy (oz t)</Label>
          <Input id="oz" type="number" inputMode="decimal" step="0.1" min="0"
            value={oncia} onChange={(e) => setOncia(e.target.value)} className="mt-1" />
        </div>
        <div className="border-t pt-4 space-y-1">
          <p className="text-base">Grammi: <strong>{fmt(grammi, 4)} g</strong></p>
          <p className="text-sm text-muted-foreground">Prezzo spot oro 24KT: <strong>{fmt(goldPrice24K)} €/g</strong></p>
          <p className="text-2xl font-bold text-gold">Valore: {fmt(valoreEuro)} €</p>
          <p className="text-xs text-muted-foreground">1 oncia troy = 31,1034768 grammi (standard LBMA)</p>
        </div>
      </CardContent>
    </Card>
  );
}

function GrammiEuroTool({ goldPrice24K }: { goldPrice24K: number }) {
  const [grammi, setGrammi] = useState('10');
  const [karat, setKarat] = useState<'24KT' | '22KT' | '18KT' | '14KT' | '9KT'>('18KT');
  const opt = KARAT_OPTIONS.find((k) => k.value === karat)!;
  const grams = parseFloat(grammi.replace(',', '.')) || 0;
  const pricePerG = goldPrice24K * opt.mult;
  const totale = pricePerG * grams;
  return (
    <Card className="border-gold/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-gold" /> Convertitore grammi → euro
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="g">Grammi</Label>
          <Input id="g" type="number" inputMode="decimal" step="0.1" min="0"
            value={grammi} onChange={(e) => setGrammi(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="kt">Caratura</Label>
          <select id="kt" value={karat} onChange={(e) => setKarat(e.target.value as any)}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
            {KARAT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="border-t pt-4 space-y-1">
          <p className="text-sm text-muted-foreground">Prezzo {karat}: <strong>{fmt(pricePerG)} €/g</strong></p>
          <p className="text-2xl font-bold text-gold">Valore: {fmt(totale)} €</p>
        </div>
      </CardContent>
    </Card>
  );
}

function QuantoVale1gTool({ goldPrice24K }: { goldPrice24K: number }) {
  return (
    <Card className="border-gold/30">
      <CardHeader>
        <CardTitle>Valore di 1 grammo d'oro oggi</CardTitle>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2">Caratura</th>
              <th className="text-right py-2">€/grammo</th>
            </tr>
          </thead>
          <tbody>
            {KARAT_OPTIONS.map((o) => (
              <tr key={o.value} className="border-b last:border-0">
                <td className="py-2">{o.label}</td>
                <td className="py-2 text-right font-mono font-semibold">
                  {fmt(goldPrice24K * o.mult)} €
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-muted-foreground mt-3">
          Quotazione spot LBMA aggiornata in tempo reale. I compro oro applicano uno spread del 5-15%.
        </p>
      </CardContent>
    </Card>
  );
}

function QuantoVale18ktTool({ goldPrice24K }: { goldPrice24K: number }) {
  const [grammi, setGrammi] = useState('10');
  const grams = parseFloat(grammi.replace(',', '.')) || 0;
  const pricePerG = goldPrice24K * PURITY_MULTIPLIERS['18KT'];
  const totale = pricePerG * grams;
  return (
    <Card className="border-gold/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-gold" /> Calcolo valore oro 18KT
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-muted/40 rounded-md p-4">
          <p className="text-sm text-muted-foreground">Prezzo 1g oro 18KT (750‰):</p>
          <p className="text-3xl font-bold text-gold">{fmt(pricePerG)} €/g</p>
        </div>
        <div>
          <Label htmlFor="g18">Calcola per quanti grammi?</Label>
          <Input id="g18" type="number" inputMode="decimal" step="0.1" min="0"
            value={grammi} onChange={(e) => setGrammi(e.target.value)} className="mt-1" />
        </div>
        <div className="border-t pt-4">
          <p className="text-2xl font-bold text-gold">Valore: {fmt(totale)} €</p>
          <p className="text-xs text-muted-foreground mt-1">
            Spot 24KT: {fmt(goldPrice24K)} €/g × 0,750 = {fmt(pricePerG)} €/g (18KT)
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
